import { useState, useContext, useRef, useEffect, useMemo } from "react";
import TicketsTable from "./Table";
import {
  useGetAllTickets,
  useGetTicketColumns,
  useGetTicketEmailCount,
  useSendTicketEmails,
} from "../../api/tickets";
import { confirmSendTicketEmails } from "./sendTicketEmails";
import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import { useDebouncedValue } from "@mantine/hooks";
import { useSetActionButtons } from "../../services/ActionButtonsContext";
import { IconMail, IconPlus } from "@tabler/icons-react";
import { useGetEvent } from "../../api/event";
import { Loader } from "@mantine/core";
import AttendeeLimitBanner from "../AttendeeLimitBanner";

function Tickets() {
  const tableRef = useRef();

  const handleAddNewTicket = () => {
    tableRef.current?.addNewRow();
  };

  const { user } = useContext(AuthContext);
  const currentWorkspace = getCurrentWorkspace(user);
  const currentEventId = currentWorkspace.eventId;

  const { data: event } = useGetEvent(currentEventId);
  const { data: ticketEmails } = useGetTicketEmailCount({
    eventId: currentEventId,
  });
  const sendTicketEmails = useSendTicketEmails({ eventId: currentEventId });

  const [pagination, setPagination] = useState({ page: 1, limit: 50 });
  const [sorting, setSorting] = useState({
    columnAccessor: "firstName",
    direction: "asc",
  });
  const [filter, setFilter] = useState(null);
  const [debouncedFilter] = useDebouncedValue(filter, 200);

  const { data, isPending } = useGetAllTickets({
    eventId: currentEventId,
    ...pagination,
    sort: sorting,
    filter: debouncedFilter,
  });

  const {
    data: columnsData,
    isPending: columnsPending,
    isLoading: columnsLoading,
  } = useGetTicketColumns({
    eventId: currentEventId,
  });

  const customColumns = columnsData?.filter(
    (column) => column.isCustom && column.isVisible === true
  );

  const onFilterChange = (setter) => (value) => {
    setter(value);
    setPagination({ ...pagination, page: 1 });
  };

  const pendingTicketEmails = ticketEmails?.pending ?? 0;
  const actions = useMemo(
    () => [
      {
        label: "Send ticket emails",
        onClick: () =>
          confirmSendTicketEmails({
            pending: pendingTicketEmails,
            withoutEmail: ticketEmails?.withoutEmail ?? 0,
            send: sendTicketEmails.mutate,
          }),
        icon: <IconMail />,
        variant: "outline",
        loading: sendTicketEmails.isPending,
      },
      {
        label: "Create",
        onClick: handleAddNewTicket,
        icon: <IconPlus />,
        disabled: event?.isHardBlocked,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      event?.isHardBlocked,
      pendingTicketEmails,
      ticketEmails?.withoutEmail,
      sendTicketEmails.isPending,
    ]
  );

  useSetActionButtons(actions);

  // When changing an event, we need to reset pagination, filter and sorting
  useEffect(() => {
    setPagination({ page: 1, limit: 50 });
    setFilter(null);
    setSorting({ columnAccessor: "firstName", direction: "asc" });
  }, [currentEventId]);

  if (columnsPending || columnsLoading) {
    return <Loader />;
  }

  return (
    <>
      <AttendeeLimitBanner
        ticketsCount={event?.ticketsCount}
        attendeeLimit={event?.attendeeLimit}
        isAttendeeLimitReached={event?.isAttendeeLimitReached}
        isHardBlocked={event?.isHardBlocked}
      />
      <TicketsTable
        ref={tableRef}
        data={data}
        pagination={{ data: pagination, setPagination }}
        sorting={{ data: sorting, setSorting }}
        isPending={isPending}
        filter={{ data: filter, setFilter: onFilterChange(setFilter) }}
        customColumns={customColumns}
        columnsData={columnsData}
      />
    </>
  );
}

export default Tickets;
